import { Plus } from "lucide-react";
import { useState } from "react";
import { Button } from "../../../components/button";
import PageHeader from "../../../components/common/PageHeader";
import { DialogV2 } from "../../../components/dialog-v2";
import { DataTable } from "../../../components/table/data-table/DataTable";
import { OrganizationEditor } from "../components/OrganizationEditor";
import { createOrganizationTableColumns } from "../components/organizationTableColumns";
import { OrganizationsToolbar } from "../components/OrganizationsToolbar";
import { useOrganizations } from "../hooks/useOrganizations";
import type { Organization, OrganizationPayload } from "../types/organization.types";

export function OrganizationsPage() {
  const organizations = useOrganizations();
  const [editing, setEditing] = useState<Organization | null>(null);
  const [isDialogOpen, setDialogOpen] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const openEditor = (organization?: Organization) => {
    setEditing(organization ?? null);
    setActionError(null);
    setDialogOpen(true);
  };

  const saveOrganization = async (payload: OrganizationPayload) => {
    setSaving(true);
    setActionError(null);
    try {
      await organizations.save(editing?.id, payload);
      setDialogOpen(false);
    } catch (requestError) {
      setActionError(
        requestError instanceof Error ? requestError.message : "Unable to save organization",
      );
    } finally {
      setSaving(false);
    }
  };

  const remove = async (organization: Organization) => {
    if (!window.confirm(`Delete ${organization.name}? This cannot be undone.`)) return;

    setActionError(null);
    try {
      await organizations.remove(organization.id);
    } catch (requestError) {
      setActionError(
        requestError instanceof Error ? requestError.message : "Unable to delete organization",
      );
    }
  };

  const columns = createOrganizationTableColumns({
    onEdit: openEditor,
    onDelete: (organization) => void remove(organization),
  });

  return (
    <div className="min-h-screen bg-background p-4 md:p-6">
      <PageHeader
        title="Organizations"
        subtitle="Manage city organizations."
        actions={
          <Button onClick={() => openEditor()}>
            <Plus className="mr-2 h-4 w-4" />
            Add Organization
          </Button>
        }
      />
      <div className="rounded-xl border border-[#DCDCDC] bg-white p-4 shadow-sm">
        <OrganizationsToolbar
          keyword={organizations.keyword}
          onKeywordChange={(keyword) => {
            organizations.setKeyword(keyword);
            organizations.setPage(1);
          }}
          onRefresh={() => void organizations.refetch()}
        />
        {actionError ? <p className="mb-3 text-sm text-[#EC5454]">{actionError}</p> : null}
        <DataTable
          columns={columns}
          dataSource={organizations.items}
          rowKey="id"
          loading={organizations.loading}
          error={organizations.error}
          onRetry={() => void organizations.refetch()}
          emptyLabel="No organizations found."
          pagination={{
            page: organizations.page,
            totalPages: Math.max(1, Math.ceil(organizations.total / 20)),
            onPageChange: organizations.setPage,
          }}
        />
      </div>
      <DialogV2
        open={isDialogOpen}
        onClose={() => setDialogOpen(false)}
        title={`${editing ? "Edit" : "Add"} Organization`}
        maxWidth="sm"
        actions={
          <>
            <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
              Cancel
            </Button>
            <Button type="submit" form="organization-editor" disabled={saving}>
              {saving ? "Saving..." : "Save"}
            </Button>
          </>
        }
      >
        <OrganizationEditor
          formId="organization-editor"
          organization={editing}
          isOpen={isDialogOpen}
          saving={saving}
          error={actionError}
          onSubmit={saveOrganization}
        />
      </DialogV2>
    </div>
  );
}
